/**
 * Country hub + priority city SEO content for Wave 1 markets.
 */

export type MarketCity = {
  slug: string;
  name: string;
  /** Approximate map bounds [west, south, east, north] for Airbnb smoke tests */
  bounds: [number, number, number, number];
  searchQuery: string;
  seoTitle: Record<string, string>;
  seoDescription: Record<string, string>;
  keywords: Record<string, string>;
  heroTitle: Record<string, string>;
  heroSubtitle: Record<string, string>;
  body: Record<string, string[]>;
};

export type MarketCountry = {
  slug: string;
  /** Locales that should feature this hub prominently */
  primaryLocales: string[];
  name: Record<string, string>;
  currency: string;
  seoTitle: Record<string, string>;
  seoDescription: Record<string, string>;
  keywords: Record<string, string>;
  heroTitle: Record<string, string>;
  heroSubtitle: Record<string, string>;
  regulationBlurb: Record<string, string>;
  cities: MarketCity[];
};

const pick = (map: Record<string, string>, locale: string, fallback = "en-US") =>
  map[locale] || map[fallback] || Object.values(map)[0] || "";

const pickList = (
  map: Record<string, string[]>,
  locale: string,
  fallback = "en-US"
) => map[locale] || map[fallback] || Object.values(map)[0] || [];

export const MARKET_COUNTRIES: MarketCountry[] = [
  {
    slug: "spain",
    primaryLocales: ["es-ES"],
    name: {
      "en-US": "Spain",
      "es-ES": "España",
      "es-MX": "España",
      "en-CA": "Spain",
      "en-GB": "Spain",
    },
    currency: "EUR",
    seoTitle: {
      "es-ES":
        "Monitorización de alquiler turístico en España | Licencias HUT/VFT",
      "en-US": "Short-Term Rental Monitoring in Spain | Tourist Licence Compliance",
    },
    seoDescription: {
      "es-ES":
        "Software de monitorización de viviendas de uso turístico para ayuntamientos en España. Detecte anuncios en Airbnb y Booking.com y apoye el cumplimiento del registro nacional y licencias autonómicas.",
      "en-US":
        "STR monitoring for Spanish municipalities. Discover Airbnb and Booking.com tourist rentals and support national registration and regional licence compliance.",
    },
    keywords: {
      "es-ES":
        "monitorización Airbnb España, licencia HUT, vivienda uso turístico, cumplimiento alquiler turístico Barcelona Madrid",
      "en-US":
        "Airbnb monitoring Spain, tourist licence compliance, short-term rental monitoring Barcelona Madrid",
    },
    heroTitle: {
      "es-ES": "Monitorización de alquiler turístico para ayuntamientos en España",
      "en-US": "Short-term rental monitoring for Spanish municipalities",
    },
    heroSubtitle: {
      "es-ES":
        "Apoye el registro nacional de alquileres de corta duración y las licencias autonómicas (HUT, VFT, ETV, VUT) con detección continua de anuncios.",
      "en-US":
        "Support Spain’s national short-term rental registration and regional licences with continuous listing discovery across major platforms.",
    },
    regulationBlurb: {
      "es-ES":
        "Desde julio de 2025, los alquileres turísticos y de temporada requieren número de registro nacional para anunciarse. Las plataformas deben verificar credenciales. Ciudades como Barcelona aplican restricciones adicionales.",
      "en-US":
        "As of July 2025, tourist and seasonal rentals need a national registration number to advertise. Platforms must verify credentials. Cities like Barcelona add further restrictions.",
    },
    cities: [
      {
        slug: "barcelona",
        name: "Barcelona",
        bounds: [2.05, 41.32, 2.23, 41.47],
        searchQuery: "Barcelona, Spain",
        seoTitle: {
          "es-ES":
            "Monitorización Airbnb Barcelona | Cumplimiento alquiler turístico",
          "en-US": "Airbnb Monitoring Barcelona | Tourist Rental Compliance",
        },
        seoDescription: {
          "es-ES":
            "Detecte viviendas de uso turístico sin licencia en Barcelona. Monitorización de anuncios Airbnb y Booking.com para ayuntamientos e inspección.",
          "en-US":
            "Discover unlicensed tourist rentals in Barcelona. Airbnb and Booking.com listing monitoring for municipal compliance teams.",
        },
        keywords: {
          "es-ES":
            "monitorización Airbnb Barcelona, licencia turística Barcelona, VUT ilegal Barcelona",
          "en-US":
            "Airbnb monitoring Barcelona, tourist licence Barcelona, illegal short-term rental Barcelona",
        },
        heroTitle: {
          "es-ES": "Monitorización de alquiler turístico en Barcelona",
          "en-US": "Short-term rental monitoring in Barcelona",
        },
        heroSubtitle: {
          "es-ES":
            "Barcelona aplica un régimen estricto de licencias turísticas. STR Monitoring ayuda a detectar anuncios activos y priorizar el seguimiento de cumplimiento.",
          "en-US":
            "Barcelona enforces a strict tourist-licence regime. STR Monitoring helps surface active listings and prioritise compliance follow-up.",
        },
        body: {
          "es-ES": [
            "El mercado de alquiler turístico en Barcelona está muy regulado, con presión creciente sobre anuncios sin licencia válida.",
            "STR Monitoring ayuda a los equipos municipales a construir un inventario de anuncios activos en Airbnb y Booking.com dentro del término municipal.",
            "Use los hallazgos para priorizar inspecciones, documentar casos y apoyar el cumplimiento del registro y la normativa local.",
          ],
          "en-US": [
            "Barcelona’s tourist rental market is tightly regulated, with growing pressure on listings without a valid licence.",
            "STR Monitoring helps municipal teams build an inventory of active Airbnb and Booking.com listings within city boundaries.",
            "Use findings to prioritise inspections, document cases, and support registration and local compliance.",
          ],
        },
      },
      {
        slug: "madrid",
        name: "Madrid",
        bounds: [-3.83, 40.31, -3.52, 40.56],
        searchQuery: "Madrid, Spain",
        seoTitle: {
          "es-ES":
            "Monitorización Airbnb Madrid | Cumplimiento vivienda turística",
          "en-US": "Airbnb Monitoring Madrid | Tourist Housing Compliance",
        },
        seoDescription: {
          "es-ES":
            "Software de detección de viviendas turísticas en Madrid para ayuntamientos. Monitorice anuncios y apoye el cumplimiento de licencias.",
          "en-US":
            "Tourist housing discovery software for Madrid municipalities. Monitor listings and support licence compliance.",
        },
        keywords: {
          "es-ES":
            "monitorización Airbnb Madrid, vivienda turística Madrid, cumplimiento alquiler vacacional",
          "en-US":
            "Airbnb monitoring Madrid, tourist housing Madrid, vacation rental compliance",
        },
        heroTitle: {
          "es-ES": "Monitorización de vivienda turística en Madrid",
          "en-US": "Tourist housing monitoring in Madrid",
        },
        heroSubtitle: {
          "es-ES":
            "Identifique anuncios activos de alquiler turístico en Madrid y apoye inspección y licencias con datos claros.",
          "en-US":
            "Identify active tourist rental listings in Madrid and support inspection and licensing with clear data.",
        },
        body: {
          "es-ES": [
            "Madrid concentra un gran volumen de anuncios de corta estancia en plataformas digitales.",
            "La monitorización continua ayuda a detectar nuevos anuncios, cambios de dirección y reapariciones tras bajas.",
            "STR Monitoring ofrece a los ayuntamientos un punto de partida estructurado para el cumplimiento.",
          ],
          "en-US": [
            "Madrid hosts a large volume of short-stay listings on digital platforms.",
            "Continuous monitoring helps detect new listings, address changes, and relists after takedowns.",
            "STR Monitoring gives municipalities a structured starting point for compliance.",
          ],
        },
      },
    ],
  },
  {
    slug: "mexico",
    primaryLocales: ["es-MX"],
    name: {
      "en-US": "Mexico",
      "es-MX": "México",
      "es-ES": "México",
      "en-CA": "Mexico",
      "en-GB": "Mexico",
    },
    currency: "MXN",
    seoTitle: {
      "es-MX":
        "Monitoreo de hospedaje turístico en México | Cumplimiento Airbnb",
      "en-US": "Short-Term Rental Monitoring in Mexico | Airbnb Compliance",
    },
    seoDescription: {
      "es-MX":
        "Monitoreo de anuncios de hospedaje para municipios mexicanos. Detecte Airbnb y Booking.com y apoye folios, registros e impuestos de hospedaje.",
      "en-US":
        "Lodging listing monitoring for Mexican municipalities. Discover Airbnb and Booking.com stays and support registry, folio, and lodging-tax compliance.",
    },
    keywords: {
      "es-MX":
        "monitoreo Airbnb México, folio hospedaje CDMX, impuesto hospedaje Quintana Roo, cumplimiento alquiler vacacional",
      "en-US":
        "Airbnb monitoring Mexico, CDMX host registry, Quintana Roo lodging tax, vacation rental compliance",
    },
    heroTitle: {
      "es-MX": "Monitoreo de hospedaje turístico para municipios en México",
      "en-US": "Short-term rental monitoring for Mexican municipalities",
    },
    heroSubtitle: {
      "es-MX":
        "Apoye registros municipales, folios e impuestos de hospedaje con detección continua de anuncios en destinos de alta demanda.",
      "en-US":
        "Support municipal registries, host folios, and lodging taxes with continuous listing discovery in high-demand destinations.",
    },
    regulationBlurb: {
      "es-MX":
        "Ciudades como CDMX exigen registro y folio visible; estados turísticos como Quintana Roo refuerzan impuestos de hospedaje y presión sobre plataformas.",
      "en-US":
        "Cities such as Mexico City require host registration and visible folio numbers; tourism states like Quintana Roo enforce lodging taxes with platform pressure.",
    },
    cities: [
      {
        slug: "mexico-city",
        name: "Mexico City",
        bounds: [-99.35, 19.25, -98.95, 19.55],
        searchQuery: "Mexico City, Mexico",
        seoTitle: {
          "es-MX":
            "Monitoreo Airbnb CDMX | Cumplimiento folio de hospedaje",
          "en-US": "Airbnb Monitoring Mexico City | Host Folio Compliance",
        },
        seoDescription: {
          "es-MX":
            "Detecte hospedaje sin folio en la Ciudad de México. Monitoreo de anuncios Airbnb y Booking.com para autoridades municipales.",
          "en-US":
            "Discover unregistered lodging in Mexico City. Airbnb and Booking.com monitoring for municipal authorities.",
        },
        keywords: {
          "es-MX":
            "monitoreo Airbnb CDMX, folio hospedaje Ciudad de México, registro anfitriones",
          "en-US":
            "Airbnb monitoring CDMX, Mexico City host folio, short-term rental registry",
        },
        heroTitle: {
          "es-MX": "Monitoreo de hospedaje en la Ciudad de México",
          "en-US": "Lodging monitoring in Mexico City",
        },
        heroSubtitle: {
          "es-MX":
            "La CDMX exige registro de anfitriones. STR Monitoring ayuda a detectar anuncios activos y priorizar verificación de folios.",
          "en-US":
            "Mexico City requires host registration. STR Monitoring helps surface active listings and prioritise folio verification.",
        },
        body: {
          "es-MX": [
            "La Ciudad de México concentra uno de los inventarios de Airbnb más grandes de América Latina.",
            "El monitoreo continuo ayuda a identificar anuncios nuevos y sin folio visible.",
            "Los equipos municipales pueden usar los hallazgos para seguimiento de cumplimiento e impuestos.",
          ],
          "en-US": [
            "Mexico City hosts one of Latin America’s largest Airbnb inventories.",
            "Continuous monitoring helps identify new listings and those without a visible folio.",
            "Municipal teams can use findings for compliance and tax follow-up.",
          ],
        },
      },
      {
        slug: "cancun",
        name: "Cancún",
        bounds: [-86.95, 21.02, -86.72, 21.22],
        searchQuery: "Cancun, Mexico",
        seoTitle: {
          "es-MX":
            "Monitoreo Airbnb Cancún | Impuesto de hospedaje Quintana Roo",
          "en-US": "Airbnb Monitoring Cancún | Quintana Roo Lodging Tax",
        },
        seoDescription: {
          "es-MX":
            "Monitoreo de hospedaje turístico en Cancún para municipios y autoridades fiscales. Detecte anuncios y apoye el cumplimiento del impuesto de hospedaje.",
          "en-US":
            "Tourist lodging monitoring in Cancún for municipalities and tax authorities. Discover listings and support lodging-tax compliance.",
        },
        keywords: {
          "es-MX":
            "monitoreo Airbnb Cancún, impuesto hospedaje Quintana Roo, alquiler vacacional Cancún",
          "en-US":
            "Airbnb monitoring Cancun, Quintana Roo lodging tax, vacation rental Cancun",
        },
        heroTitle: {
          "es-MX": "Monitoreo de hospedaje turístico en Cancún",
          "en-US": "Tourist lodging monitoring in Cancún",
        },
        heroSubtitle: {
          "es-MX":
            "Cancún y la Riviera Maya concentran alta densidad de anuncios. Apoye registros e impuestos de hospedaje con visibilidad continua.",
          "en-US":
            "Cancún and the Riviera Maya have high listing density. Support registries and lodging taxes with continuous visibility.",
        },
        body: {
          "es-MX": [
            "El corredor Cancún–Riviera Maya es un mercado prioritario de hospedaje de corta estancia.",
            "STR Monitoring ayuda a construir un inventario de anuncios activos para seguimiento municipal.",
            "Combine detección de plataformas con sus procesos de registro e impuesto de hospedaje.",
          ],
          "en-US": [
            "The Cancún–Riviera Maya corridor is a priority short-stay lodging market.",
            "STR Monitoring helps build an inventory of active listings for municipal follow-up.",
            "Combine platform discovery with your registry and lodging-tax processes.",
          ],
        },
      },
    ],
  },
  {
    slug: "canada",
    primaryLocales: ["en-CA"],
    name: {
      "en-US": "Canada",
      "en-CA": "Canada",
      "en-GB": "Canada",
      "es-ES": "Canadá",
      "es-MX": "Canadá",
    },
    currency: "CAD",
    seoTitle: {
      "en-CA":
        "Short-Term Rental Monitoring in Canada | Municipal STR Compliance",
      "en-US":
        "Short-Term Rental Monitoring in Canada | Municipal STR Compliance",
    },
    seoDescription: {
      "en-CA":
        "STR monitoring for Canadian cities and local governments. Discover Airbnb and VRBO listings and support provincial registries, CITQ, and municipal licensing.",
      "en-US":
        "STR monitoring for Canadian cities and local governments. Discover Airbnb and VRBO listings and support provincial registries, CITQ, and municipal licensing.",
    },
    keywords: {
      "en-CA":
        "short-term rental monitoring Canada, BC STR registry, CITQ Quebec, Toronto Airbnb licensing, municipal STR compliance",
      "en-US":
        "short-term rental monitoring Canada, BC STR registry, CITQ Quebec, Toronto Airbnb licensing",
    },
    heroTitle: {
      "en-CA": "Short-term rental monitoring for Canadian municipalities",
      "en-US": "Short-term rental monitoring for Canadian municipalities",
    },
    heroSubtitle: {
      "en-CA":
        "Support BC’s provincial registry, Quebec’s CITQ, and municipal licensing programmes with continuous Airbnb and VRBO discovery.",
      "en-US":
        "Support BC’s provincial registry, Quebec’s CITQ, and municipal licensing programmes with continuous Airbnb and VRBO discovery.",
    },
    regulationBlurb: {
      "en-CA":
        "Canada combines provincial registries (e.g. British Columbia) and municipal licensing (e.g. Toronto) with strong enforcement interest from cities and condo corporations.",
      "en-US":
        "Canada combines provincial registries (e.g. British Columbia) and municipal licensing (e.g. Toronto) with strong enforcement interest from cities and condo corporations.",
    },
    cities: [
      {
        slug: "toronto",
        name: "Toronto",
        bounds: [-79.64, 43.58, -79.11, 43.86],
        searchQuery: "Toronto, Canada",
        seoTitle: {
          "en-CA":
            "Airbnb Monitoring Toronto | Short-Term Rental Licensing Compliance",
          "en-US":
            "Airbnb Monitoring Toronto | Short-Term Rental Licensing Compliance",
        },
        seoDescription: {
          "en-CA":
            "Discover unlicensed short-term rentals in Toronto. Monitor Airbnb and VRBO listings for municipal licensing and bylaw enforcement.",
          "en-US":
            "Discover unlicensed short-term rentals in Toronto. Monitor Airbnb and VRBO listings for municipal licensing and bylaw enforcement.",
        },
        keywords: {
          "en-CA":
            "Airbnb monitoring Toronto, short-term rental licence Toronto, STR compliance Toronto",
          "en-US":
            "Airbnb monitoring Toronto, short-term rental licence Toronto, STR compliance Toronto",
        },
        heroTitle: {
          "en-CA": "Short-term rental monitoring in Toronto",
          "en-US": "Short-term rental monitoring in Toronto",
        },
        heroSubtitle: {
          "en-CA":
            "Toronto requires short-term rental licensing. STR Monitoring helps surface active listings for bylaw and licensing follow-up.",
          "en-US":
            "Toronto requires short-term rental licensing. STR Monitoring helps surface active listings for bylaw and licensing follow-up.",
        },
        body: {
          "en-CA": [
            "Toronto is one of Canada’s densest short-term rental markets with active municipal licensing.",
            "Continuous monitoring helps identify new and returning listings across Airbnb and VRBO.",
            "Use findings to support licensing checks and neighbourhood protection programmes.",
          ],
          "en-US": [
            "Toronto is one of Canada’s densest short-term rental markets with active municipal licensing.",
            "Continuous monitoring helps identify new and returning listings across Airbnb and VRBO.",
            "Use findings to support licensing checks and neighbourhood protection programmes.",
          ],
        },
      },
    ],
  },
  {
    slug: "united-kingdom",
    primaryLocales: ["en-GB"],
    name: {
      "en-US": "United Kingdom",
      "en-GB": "United Kingdom",
      "en-CA": "United Kingdom",
      "es-ES": "Reino Unido",
      "es-MX": "Reino Unido",
    },
    currency: "GBP",
    seoTitle: {
      "en-GB":
        "Short-Term Let Monitoring in the UK | Holiday Let Compliance",
      "en-US":
        "Short-Term Let Monitoring in the UK | Holiday Let Compliance",
    },
    seoDescription: {
      "en-GB":
        "Short-term let and holiday let monitoring for UK councils. Discover Airbnb and VRBO listings and support London night caps, Scotland STL licensing, and registration rules.",
      "en-US":
        "Short-term let and holiday let monitoring for UK councils. Discover Airbnb and VRBO listings and support London night caps, Scotland STL licensing, and registration rules.",
    },
    keywords: {
      "en-GB":
        "short-term let monitoring UK, holiday let compliance, Airbnb monitoring London, Scotland STL licensing",
      "en-US":
        "short-term let monitoring UK, holiday let compliance, Airbnb monitoring London",
    },
    heroTitle: {
      "en-GB": "Short-term let monitoring for UK councils",
      "en-US": "Short-term let monitoring for UK councils",
    },
    heroSubtitle: {
      "en-GB":
        "Support London’s 90-night rules, Scotland’s STL licensing, and emerging England registration with continuous listing discovery.",
      "en-US":
        "Support London’s 90-night rules, Scotland’s STL licensing, and emerging England registration with continuous listing discovery.",
    },
    regulationBlurb: {
      "en-GB":
        "The UK mixes local night caps (London), mandatory short-term let licensing (Scotland), and national registration reforms in England—creating strong demand for listing visibility.",
      "en-US":
        "The UK mixes local night caps (London), mandatory short-term let licensing (Scotland), and national registration reforms in England—creating strong demand for listing visibility.",
    },
    cities: [
      {
        slug: "london",
        name: "London",
        bounds: [-0.51, 51.28, 0.33, 51.69],
        searchQuery: "London, United Kingdom",
        seoTitle: {
          "en-GB":
            "Airbnb Monitoring London | Short-Term Let & 90-Night Compliance",
          "en-US":
            "Airbnb Monitoring London | Short-Term Let & 90-Night Compliance",
        },
        seoDescription: {
          "en-GB":
            "Discover short-term lets in London for council compliance. Monitor Airbnb and VRBO listings against 90-night and licensing rules.",
          "en-US":
            "Discover short-term lets in London for council compliance. Monitor Airbnb and VRBO listings against 90-night and licensing rules.",
        },
        keywords: {
          "en-GB":
            "Airbnb monitoring London, short-term let London, 90 night rule London, holiday let compliance",
          "en-US":
            "Airbnb monitoring London, short-term let London, 90 night rule London",
        },
        heroTitle: {
          "en-GB": "Short-term let monitoring in London",
          "en-US": "Short-term let monitoring in London",
        },
        heroSubtitle: {
          "en-GB":
            "London’s 90-night rules and dense listing supply make continuous monitoring essential for council enforcement teams.",
          "en-US":
            "London’s 90-night rules and dense listing supply make continuous monitoring essential for council enforcement teams.",
        },
        body: {
          "en-GB": [
            "London is among the world’s largest short-term let markets with layered local rules.",
            "STR Monitoring helps councils build an inventory of active Airbnb and VRBO listings.",
            "Use findings to support night-cap enforcement, licensing checks, and resident complaints.",
          ],
          "en-US": [
            "London is among the world’s largest short-term let markets with layered local rules.",
            "STR Monitoring helps councils build an inventory of active Airbnb and VRBO listings.",
            "Use findings to support night-cap enforcement, licensing checks, and resident complaints.",
          ],
        },
      },
    ],
  },
];

export const getMarketCountry = (slug: string): MarketCountry | undefined =>
  MARKET_COUNTRIES.find((c) => c.slug === slug);

export const getMarketCity = (
  countrySlug: string,
  citySlug: string
): { country: MarketCountry; city: MarketCity } | undefined => {
  const country = getMarketCountry(countrySlug);
  if (!country) return undefined;
  const city = country.cities.find((c) => c.slug === citySlug);
  if (!city) return undefined;
  return { country, city };
};

export const localizedMarketText = pick;
export const localizedMarketList = pickList;

/** Flat list of all market URLs (unprefixed) for sitemaps. */
export const allMarketPaths = (): string[] => {
  const paths: string[] = [];
  for (const country of MARKET_COUNTRIES) {
    paths.push(`/markets/${country.slug}`);
    for (const city of country.cities) {
      paths.push(`/markets/${country.slug}/${city.slug}`);
    }
  }
  return paths;
};
